import React, { useState, useEffect } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { History, Download, ArrowRight } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', green: '#1D9E75', red: '#E24B4A', amber: '#B27516', slate: '#5C6B7A' };
const ACTION_STYLE = {
  created_and_scored: { label: 'Created & Scored', color: C.blue },
  draft_created: { label: 'Draft Started', color: C.slate },
  draft_edit: { label: 'Draft Edited', color: C.amber },
  manual_decision: { label: 'Manual Decision', color: C.green },
};
const fmtVal = v => {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) return v.length ? v.map(x => (x && typeof x === 'object') ? (x.name || JSON.stringify(x)) : x).join(', ') : '—';
  if (typeof v === 'object') return v.name || JSON.stringify(v);
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
};

export const AuditTrail = ({ submissionId }) => {
  const api = useApi();
  const [data, setData] = useState(null);
  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    api.get(`/submissions/${submissionId}/audit`)
      .then(r => setData(r.data))
      .catch(() => setData({ events: [] }));
  }, [api, submissionId]);

  if (!data) return <p className="text-xs py-2" style={{ color: C.slate }}>Loading audit trail…</p>;
  return (
    <div data-testid={`audit-trail-${submissionId}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider" style={{ color: C.slate }}>
          <History className="w-3 h-3" />Audit Trail ({data.events.length} events)
        </p>
        {data.events.length > 0 && (
          <a href={`${API_BASE}/api/submissions/${submissionId}/audit/export`} target="_blank" rel="noreferrer"
            data-testid="audit-export-csv"
            className="flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-medium transition-colors hover:bg-blue-50"
            style={{ border: '1px solid rgba(26,76,124,0.3)', color: C.blue }}>
            <Download className="w-3 h-3" />Export CSV
          </a>
        )}
      </div>
      {data.events.length === 0 && <p className="text-xs py-2" style={{ color: C.slate }}>No audit events recorded for this submission.</p>}
      <div className="space-y-2">
        {data.events.map(e => {
          const st = ACTION_STYLE[e.action] || { label: e.action, color: C.slate };
          return (
            <div key={e.audit_id} className="rounded-sm border bg-white p-3" style={{ borderColor: 'rgba(136,150,168,0.15)' }} data-testid={`audit-event-${e.audit_id}`}>
              <div className="flex flex-wrap items-center gap-2 mb-1.5">
                <span className="px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ background: st.color + '15', color: st.color }}>{st.label}</span>
                <span className="text-[11px] font-medium" style={{ color: C.navy }}>{e.user_name}</span>
                <span className="text-[9px] uppercase" style={{ color: C.slate }}>{e.user_role?.replace(/_/g, ' ')}</span>
                <span className="text-[10px] ml-auto font-mono" style={{ color: C.slate }}>
                  {new Date(e.at).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </span>
              </div>
              <div className="space-y-0.5">
                {e.changes.map((c, i) => (
                  <div key={i} className="flex items-center gap-2 text-[11px]">
                    <span className="w-44 truncate font-medium" style={{ color: C.slate }}>{c.field?.replace(/_/g, ' ')}</span>
                    <span className="font-mono truncate max-w-[180px]" style={{ color: c.before == null ? '#B8C2CC' : C.red }}>{fmtVal(c.before)}</span>
                    <ArrowRight className="w-3 h-3 shrink-0" style={{ color: C.slate }} />
                    <span className="font-mono truncate max-w-[220px] font-semibold" style={{ color: C.green }}>{fmtVal(c.after)}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AuditTrail;
